import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useRef, useCallback } from 'react';

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  read_at: string | null;
}

export const useMessages = (conversationId: string | null) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const channelRef = useRef<any>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 5;
  
  const query = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: async () => {
      if (!conversationId) return [];
      
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data as Message[];
    },
    enabled: !!conversationId && !!user,
  });

  const cleanup = useCallback(() => {
    if (channelRef.current) {
      console.log('🧹 Cleaning up channel for conversation:', conversationId);
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, [conversationId]);

  const setupRealtime = useCallback(() => {
    if (!conversationId || !user) return;

    cleanup(); // Clean up any existing subscription first

    console.log(`🔧 Setting up real-time for conversation: ${conversationId} (attempt ${retryCountRef.current + 1})`);

    const channel = supabase
      .channel(`messages-${conversationId}-${Date.now()}`, {
        config: {
          broadcast: { self: false },
          presence: { key: user.id },
        },
      })
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          console.log('📨 NEW MESSAGE RECEIVED LIVE:', payload.new);
          const newMessage = payload.new as Message;
          
          // Immediately update the messages cache
          queryClient.setQueryData(['messages', conversationId], (old: Message[] | undefined) => {
            if (!old) return [newMessage];
            
            // Check if message already exists to prevent duplicates
            const exists = old.some(msg => msg.id === newMessage.id);
            if (exists) {
              console.log('Message already exists in cache, skipping duplicate');
              return old;
            }
            
            console.log('Adding new message to cache');
            return [...old, newMessage];
          });
          
          // Update conversations list to reflect new activity
          queryClient.setQueryData(['conversations'], (oldConversations: any) => {
            if (!oldConversations) return oldConversations;
            return oldConversations.map((conv: any) => 
              conv.id === conversationId 
                ? { ...conv, updated_at: new Date().toISOString() }
                : conv
            );
          });

          // Invalidate unread counts
          queryClient.invalidateQueries({ queryKey: ['unread-messages-count'] });
          queryClient.invalidateQueries({ queryKey: ['conversation-unread-count', conversationId] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          console.log('📝 Message updated live:', payload.new);
          const updatedMessage = payload.new as Message;
          
          queryClient.setQueryData(['messages', conversationId], (old: Message[] | undefined) => {
            if (!old) return old;
            return old.map(msg => 
              msg.id === updatedMessage.id ? updatedMessage : msg
            );
          });

          // Invalidate unread counts when messages are marked as read
          queryClient.invalidateQueries({ queryKey: ['unread-messages-count'] });
          queryClient.invalidateQueries({ queryKey: ['conversation-unread-count', conversationId] });
        }
      )
      .subscribe((status, error) => {
        console.log(`🔗 Real-time status for ${conversationId}:`, status);
        
        if (error) {
          console.error('❌ Real-time subscription error:', error);
          
          // Retry with exponential backoff
          if (retryCountRef.current < maxRetries) {
            const retryDelay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30000);
            console.log(`⏳ Retrying connection in ${retryDelay}ms...`);
            
            setTimeout(() => {
              retryCountRef.current++;
              setupRealtime();
            }, retryDelay);
          } else {
            console.error('❌ Max retries reached, giving up on real-time connection');
          }
        } else if (status === 'SUBSCRIBED') {
          console.log('✅ Real-time connected successfully!');
          retryCountRef.current = 0; // Reset retry count on successful connection
        } else if (status === 'CLOSED' || status === 'TIMED_OUT') {
          console.log('⚠️ Real-time connection lost, will retry...');
          
          // Retry immediately for closed/timeout
          if (retryCountRef.current < maxRetries) {
            retryCountRef.current++;
            setTimeout(setupRealtime, 1000);
          }
        }
      });

    channelRef.current = channel;
  }, [conversationId, user, queryClient, cleanup]);

  // Set up real-time subscription
  useEffect(() => {
    if (!conversationId || !user) {
      cleanup();
      return;
    }

    setupRealtime();
    return cleanup;
  }, [conversationId, user, setupRealtime, cleanup]);

  return query;
};

export const useUnreadMessagesCount = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['unread-messages-count', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      
      const { data, error } = await supabase
        .from('messages')
        .select('id, conversation_id')
        .is('read_at', null)
        .neq('sender_id', user.id);
      
      if (error) throw error;
      
      // Filter messages from conversations where user is a participant
      const { data: conversations } = await supabase
        .from('conversations')
        .select('id')
        .or(`buyer_id.eq.${user.id},breeder_id.eq.${user.id}`);
      
      if (!conversations) return 0;
      
      const conversationIds = conversations.map(c => c.id);
      const unreadCount = data.filter(msg => conversationIds.includes(msg.conversation_id)).length;
      
      return unreadCount;
    },
    enabled: !!user,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useConversationUnreadCount = (conversationId: string) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['conversation-unread-count', conversationId, user?.id],
    queryFn: async () => {
      if (!user || !conversationId) return 0;
      
      const { data, error } = await supabase
        .from('messages')
        .select('id')
        .eq('conversation_id', conversationId)
        .is('read_at', null)
        .neq('sender_id', user.id);
      
      if (error) throw error;
      return data.length;
    },
    enabled: !!user && !!conversationId,
  });
};

export const useSendMessage = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      conversation_id,
      content,
    }: {
      conversation_id: string;
      content: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('messages')
        .insert({
          conversation_id,
          sender_id: user.id,
          content,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Immediately add the message to the local cache so it appears instantly
      queryClient.setQueryData(['messages', data.conversation_id], (old: Message[] | undefined) => {
        if (!old) return [data as Message];
        // Check if message already exists to prevent duplicates
        const messageExists = old.some(msg => msg.id === data.id);
        if (messageExists) return old;
        return [...old, data as Message];
      });
      
      // Update conversations list timestamp and ensure conversation appears in list
      queryClient.setQueryData(['conversations'], (oldConversations: any) => {
        if (!oldConversations) return oldConversations;
        return oldConversations.map((conv: any) => 
          conv.id === data.conversation_id 
            ? { ...conv, updated_at: new Date().toISOString() }
            : conv
        );
      });

      // Force refresh conversations list to ensure new conversations appear
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      
      console.log('✅ Message sent successfully:', data);
    },
    onError: (error) => {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    },
  });
};

// Create a new hook to manually mark messages as read
export const useMarkMessagesAsRead = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (conversationId: string) => {
      if (!user || !conversationId) return;
      
      // Get unread messages for this conversation
      const { data: unreadMessages, error: fetchError } = await supabase
        .from('messages')
        .select('id')
        .eq('conversation_id', conversationId)
        .is('read_at', null)
        .neq('sender_id', user.id);
      
      if (fetchError) throw fetchError;
      
      if (!unreadMessages || unreadMessages.length === 0) return;
      
      // Mark all unread messages as read
      const { error: updateError } = await supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .is('read_at', null)
        .neq('sender_id', user.id);
      
      if (updateError) throw updateError;
      
      return unreadMessages.length;
    },
    onSuccess: () => {
      // Update unread count queries
      queryClient.invalidateQueries({ queryKey: ['unread-messages-count'] });
      queryClient.invalidateQueries({ queryKey: ['conversation-unread-count'] });
    },
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (messageId: string) => {
      const { error } = await supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .eq('id', messageId);
      
      if (error) throw error;
    },
    onSuccess: (_, messageId) => {
      // Update the specific message in cache
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['unread-messages-count'] });
      queryClient.invalidateQueries({ queryKey: ['conversation-unread-count'] });
    },
  });
};
